// macOS-style magnifying dock (adapted from React Bits' <Dock />): the panel
// flows in the parent layout instead of self-positioning, so the bottom nav
// can sit it next to the filter pill. Desktop-only surface.
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
  type SpringOptions,
  type MotionValue,
} from 'motion/react'
import { useRef, useState, type ReactNode } from 'react'

import './Dock.css'

export interface DockItemData {
  icon: ReactNode
  label: ReactNode
  onClick: () => void
  className?: string
}

interface DockItemProps {
  item: DockItemData
  mouseX: MotionValue<number>
  spring: SpringOptions
  distance: number
  magnification: number
  baseItemSize: number
}

function DockItem({ item, mouseX, spring, distance, magnification, baseItemSize }: DockItemProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)

  const mouseDistance = useTransform(mouseX, (val) => {
    const rect = ref.current?.getBoundingClientRect() ?? { x: 0, width: baseItemSize }
    return val - rect.x - baseItemSize / 2
  })

  const targetSize = useTransform(mouseDistance, [-distance, 0, distance], [baseItemSize, magnification, baseItemSize])
  const size = useSpring(targetSize, spring)

  return (
    <motion.div
      ref={ref}
      style={{ width: size, height: size }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={item.onClick}
      className={`dock-item ${item.className ?? ''}`}
      tabIndex={0}
      role="button"
      aria-label={typeof item.label === 'string' ? item.label : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          item.onClick()
        }
      }}
    >
      <div className="dock-icon">{item.icon}</div>
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: -10 }}
            exit={{ opacity: 0, y: 0 }}
            transition={{ duration: 0.2 }}
            className="dock-label"
            role="tooltip"
            style={{ x: '-50%' }}
          >
            {item.label}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default function Dock({
  items,
  className = '',
  spring = { mass: 0.1, stiffness: 150, damping: 12 },
  magnification = 54,
  distance = 110,
  panelHeight = 56,
  baseItemSize = 44,
}: {
  items: DockItemData[]
  className?: string
  spring?: SpringOptions
  magnification?: number
  distance?: number
  panelHeight?: number
  baseItemSize?: number
}) {
  const mouseX = useMotionValue(Infinity)

  return (
    <motion.div
      onMouseMove={({ pageX }) => mouseX.set(pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      className={`dock-panel ${className}`}
      style={{ height: panelHeight }}
      role="toolbar"
      aria-label="Scene controls"
    >
      {items.map((item, index) => (
        <DockItem
          key={index}
          item={item}
          mouseX={mouseX}
          spring={spring}
          distance={distance}
          magnification={magnification}
          baseItemSize={baseItemSize}
        />
      ))}
    </motion.div>
  )
}

